import React, { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { uniqueId } from 'lodash';
import './styles.css';
import { Button, Card, Col, message, Space, Steps, Typography, Alert, Statistic } from 'antd';
import {
  SaveOutlined,
  EyeOutlined,
  WarningOutlined,
  MinusCircleFilled
} from '@ant-design/icons';
import moment from 'moment';

// Existing imports
import { useCampaignRequestorContext } from '../../../core/components/campaignRequestor/useCampaignRequestorContext';
import StatusTag from '../Campaigns/shared/StatusTag';
import CampaignRequestorCloneDialog from './CampaignRequestorCloneDialog';

// New optimized components and hooks
import { CampaignSaveProgress } from './components/CampaignSaveProgress';
import { useCampaignSaveManager } from './hooks/useCampaignSaveManager';
import { useMemoryOptimizedState } from './hooks/useMemoryOptimizedState';

// Utils
import { cleanLineItemsForCloning } from './CampaignRequestorUtil';


const { Step } = Steps;
const { Text } = Typography;

const defaultSteps = [
  {
    title: 'Details',
    bookmarkId: 'requester-details',
  },
  {
    title: 'Duration & Budget',
    bookmarkId: 'requester-duration-budget',
  },
  {
    title: 'Categories',
    bookmarkId: 'requester-categories',
  },
  {
    title: 'Attribution Reporting',
    bookmarkId: 'requester-attribution-reporting',
  },
];

/**
 * Optimized Campaign Requestor Summary with batch processing
 * Enhanced with performance monitoring and progress tracking
 */
const CampaignRequestorSummaryOptimized = ({
  formValues,
  validationState,
  helpers,
  mode,
  handleInitialValues
}) => {
  const [steps, setSteps] = useState(defaultSteps);
  const [activeSteps, setActiveSteps] = useState({});
  const [visibleCloneDialog, setVisibleCloneDialog] = useState(false);
  const [performanceWarnings, setPerformanceWarnings] = useState([]);

  const {
    selectedAdvertiser,
    currentCampaignOrder,
    loadingCreateCampaignOrder,
    loadingUpdateCampaignOrder,
    setSelectedAdvertiser,
    loadingBasicTagActivity,
    showActivityPopconfirm,
  } = useCampaignRequestorContext();

  // New optimization hooks
  const { saveCampaign, cancelSave, retryFailedBatches, saveProgress, isSaving } = useCampaignSaveManager();
  const { getMemoryStats } = useMemoryOptimizedState();

  const history = useHistory();
  const campaignName = formValues.name;

  // Build dynamic steps based on campaigns and line items using reactive formValues
  useEffect(() => {
    const updatedSteps = [...defaultSteps];
    const campaigns = formValues.campaigns || [];

    campaigns.forEach((campaign, campaignIndex) => {
      // Add campaign step
      updatedSteps.push({
        title: campaign.name || `Campaign ${campaignIndex + 1}`,
        bookmarkId: `requester-campaign-${campaignIndex}`,
        type: 'campaign',
        campaignIndex,
      });

      // Add nested line item steps
      const lineItems = campaign.lineItems || [];
      lineItems.forEach((lineItem, lineItemIndex) => {
        updatedSteps.push({
          title: lineItem.name || `Line Item ${lineItemIndex + 1}`,
          bookmarkId: `requester-line-item-${campaignIndex}-${lineItemIndex}`,
          type: 'lineItem',
          campaignIndex,
          lineItemIndex,
          isNested: true,
        });
      });
    });

    setSteps(updatedSteps);
  }, [formValues.campaigns]);

  // Update active steps based on validation state
  useEffect(() => {
    const campaigns = formValues.campaigns || [];
    const updatedActiveSteps = {
      details: validationState.hasRequiredDetails,
      durationAndBudget: validationState.hasValidBudget && validationState.hasValidDates,
      categories: validationState.hasValidCategories,
      attributionReporting: validationState.hasValidAttributionReporting,
    };

    // Add campaign and line item validation states
    campaigns.forEach((campaign, campaignIndex) => {
      // Campaign-level validation
      const campaignHasName = !!(campaign.name && campaign.name.trim());
      const campaignHasType = !!(campaign.type);
      const campaignHasValidBudget = !!(campaign.budget && campaign.budget > 0);

      const lineItems = campaign.lineItems || [];
      let allLineItemsValid = lineItems.length > 0;

      // Validate each line item
      lineItems.forEach((lineItem, lineItemIndex) => {
        const hasName = !!(lineItem.name && lineItem.name.trim());
        const hasValidBudget = !!(lineItem.budgetTotal > 0);
        const hasTargetingType = !!(lineItem.targetingType);
        const hasValidDates = !!(lineItem.startDate && lineItem.endDate);

        // Check if targeting is properly configured based on type
        let hasValidTargeting = false;
        if (lineItem.targetingType === 'COUNTRY') {
          hasValidTargeting = !!(lineItem.targetingCountries);
        } else if (lineItem.targetingType === 'REGION') {
          hasValidTargeting = !!(lineItem.targetingRegions && lineItem.targetingRegions.length > 0);
        } else if (lineItem.targetingType === 'LOCAL') {
          hasValidTargeting = !!(lineItem.targetingLocations && lineItem.targetingLocations.length > 0);
        }

        // Ad group/creatives validation
        const hasCreatives = !!(lineItem.adGroup?.adGroupCreatives?.length > 0);

        // Clickthrough URL validation for display campaigns
        const isVideoType = formValues.type === 8;
        const hasValidClickthrough = isVideoType ||
          (lineItem.adGroup?.adGroupCreatives?.every(creative =>
            creative.clickthrough && creative.clickthrough.trim() !== ''
          ) || false);

        const lineItemValid = hasName && hasValidBudget && hasTargetingType &&
          hasValidDates && hasValidTargeting && hasCreatives && hasValidClickthrough;

        updatedActiveSteps[`lineItem-${campaignIndex}-${lineItemIndex}`] = lineItemValid;

        if (!lineItemValid) {
          allLineItemsValid = false;
        }
      });

      // Campaign is valid if it has required fields AND all line items are valid
      updatedActiveSteps[`campaign-${campaignIndex}`] = campaignHasName && campaignHasType &&
        campaignHasValidBudget && allLineItemsValid;
    });

    setActiveSteps(updatedActiveSteps);
  }, [formValues, validationState]);

  // Helper function to check if a step is active
  const isActive = (step, stepIndex) => {
    if (stepIndex < 4) {
      const stepKeys = ['details', 'durationAndBudget', 'categories', 'attributionReporting'];
      return activeSteps[stepKeys[stepIndex]];
    }

    // For campaign and line item steps
    if (step.type === 'campaign') {
      return activeSteps[`campaign-${step.campaignIndex}`];
    } else if (step.type === 'lineItem') {
      return activeSteps[`lineItem-${step.campaignIndex}-${step.lineItemIndex}`];
    }

    return false;
  };

  // Performance monitoring
  useEffect(() => {
    const warnings = [];
    const lineItemCount = formValues.campaigns?.length || 0;
    const memoryStats = getMemoryStats();

    // Check for performance issues
    if (lineItemCount > 20) {
      warnings.push(`Large campaign detected: ${lineItemCount} line items may impact performance`);
    }

    if (memoryStats.isNearLimit) {
      warnings.push(`Memory usage high: ${memoryStats.usagePercentage}% of limit`);
    }

    // Count total creatives
    let totalCreatives = 0;
    formValues.campaigns?.forEach(campaign => {
      if (campaign.adGroup?.adGroupCreatives) {
        totalCreatives += campaign.adGroup.adGroupCreatives.length;
      }
    });

    if (totalCreatives > 500) {
      warnings.push(`Large creative count: ${totalCreatives} creatives may cause save delays`);
    }

    setPerformanceWarnings(warnings);
  }, [formValues, getMemoryStats]);

  // Review mode data initialization (existing logic)
  useEffect(() => {
    if (mode === 'review' && currentCampaignOrder) {

      if (handleInitialValues && typeof handleInitialValues === 'function') {
        handleInitialValues(currentCampaignOrder);
      }

      if (helpers && helpers.form && mode === 'review') {
        const reviewModeData = {
          name: currentCampaignOrder.name || '',
          description: currentCampaignOrder.description || '',
          advertiserId: currentCampaignOrder.advertiser?.id || selectedAdvertiser?.id,
          advertiser: currentCampaignOrder.advertiser || selectedAdvertiser,
          budget: currentCampaignOrder.budget || 0,
          cpm: currentCampaignOrder.cpm || 7,
          category: currentCampaignOrder.category || 'IAB7-5',
          advertiserType: currentCampaignOrder.advertiserType || 'LOCAL_DELIVERY_SERVICE',
          isRegulated: currentCampaignOrder.isRegulated !== undefined ? currentCampaignOrder.isRegulated : true,
          type: currentCampaignOrder.type || 1,
          renewalStatus: currentCampaignOrder.renewalStatus || 'EXPECTED_TO_RENEW',
          ...(formValues.campaigns && formValues.campaigns.length > 0
            ? {}
            : { campaigns: currentCampaignOrder.campaigns || [] }
          ),
          attributionTargets: currentCampaignOrder.attributionTargets || [],
          geoTargets: currentCampaignOrder.geoTargets || [],
          notes: currentCampaignOrder.notes || '',
          IONumber: currentCampaignOrder.IONumber || '',
          productCode: currentCampaignOrder.productCode || 'DIS',
        };

        helpers.form.setFieldsValue(reviewModeData);
      }
    }
  }, [mode, currentCampaignOrder, handleInitialValues, helpers, selectedAdvertiser, formValues.campaigns]);

  /**
   * Validation helper
   */
  const validateForm = useCallback(async () => {
    try {
      await helpers.validateFields();
      return true;
    } catch (error) {
      if (error.errorFields && error.errorFields[0]) {
        message.error(error.errorFields[0].errors[0]);
      }
      return false;
    }
  }, [helpers]);

  /**
   * Enhanced save function using batch processing
   */
  const handleSaveOptimized = useCallback(async () => {
    const isValid = await validateForm();
    if (!isValid) return;

    try {
      // Get all form values
      const allFormValues = helpers.form.getFieldsValue();

      // Generate draft name if campaign name is empty
      const draftName = allFormValues.name || formValues.name || `DRAFT ${uniqueId()} ${new Date().getTime()}`;

      // Update form with draft name if name was empty
      if (!allFormValues.name && !formValues.name) {
        helpers.form.setFieldsValue({ name: draftName });
      }

      const campaignData = {
        ...allFormValues,
        ...formValues,
        name: draftName, // Use draft name if original was empty
        advertiser: formValues.advertiser || selectedAdvertiser,
        advertiserId: formValues.advertiserId || selectedAdvertiser?.id,
        // Preserve existing status if updating, otherwise default to DRAFT
        status: currentCampaignOrder?.status || formValues.status || 'DRAFT',
      };

      // Use the optimized batch save manager
      const isUpdate = !!currentCampaignOrder;
      const result = await saveCampaign(campaignData, isUpdate, currentCampaignOrder, selectedAdvertiser);

      if (result.success) {
        // Update URL to include the new campaign order ID
        const campaignOrderId = result.campaignOrderId;
        history.replace(`/campaigns/requester/edit/${campaignOrderId}`);

        return { id: campaignOrderId };
      } else {
        throw result.error;
      }
    } catch (error) {
      throw error;
    }
  }, [validateForm, helpers.form, formValues, selectedAdvertiser, currentCampaignOrder, saveCampaign, history]);


  /**
   * Handle next step using the same save logic, then navigate
   */
  const handleNextStep = async () => {
    if (!validationState.isComplete) {
      message.error('Please complete all required fields before proceeding to review.');
      return;
    }

    try {
      let campaignOrderId;

      if (currentCampaignOrder) {
        // For existing campaigns, use the same save logic as Save Draft button
        await handleSaveOptimized();
        campaignOrderId = currentCampaignOrder.id;
      } else {
        // For new campaigns
        const campaignOrder = await handleSaveOptimized();
        campaignOrderId = campaignOrder.id;
      }

      // Navigate to review page
      history.push(`/campaigns/requester/review/${campaignOrderId}`);
    } catch (error) {
      message.error('Failed to save before proceeding to review.');
    }
  };

  /**
   * Calculate campaign statistics for display
   * Updated to support multi-campaign structure
   */
  const getCampaignStats = () => {
    const campaigns = formValues.campaigns || [];
    let totalCreatives = 0;
    let totalLineItems = 0;
    let appliedBudget = 0;

    // Sum up campaign budgets and count line items/creatives
    campaigns.forEach(campaign => {
      // Add campaign budget to applied budget (this is the budget allocated to each campaign)
      appliedBudget += Number(campaign.budget) || 0;

      // Count line items within this campaign
      const lineItems = campaign.lineItems || [];
      totalLineItems += lineItems.length;

      // Count creatives within all line items of this campaign
      lineItems.forEach(lineItem => {
        if (lineItem.adGroup?.adGroupCreatives) {
          totalCreatives += lineItem.adGroup.adGroupCreatives.length;
        }
      });
    });

    // Get campaign order budget
    const campaignOrderBudget = formValues.budget || currentCampaignOrder?.budget || 0;

    return {
      lineItems: totalLineItems,
      totalCreatives,
      totalBudget: appliedBudget, // This is now the sum of campaign budgets
      campaignOrderBudget,
      campaignCount: campaigns.length,
      avgBudgetPerCampaign: campaigns.length > 0 ? campaignOrderBudget / campaigns.length : 0,
    };
  };

  const stats = getCampaignStats();

  return (
    <Col className="campaign-requester-sticky" style={{ paddingBottom: '100px' }}>
      {/* Progress Modal */}
      <CampaignSaveProgress
        progress={saveProgress}
        onCancel={cancelSave}
        onRetry={retryFailedBatches}
      />

      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ whiteSpace: 'nowrap' }}>
              SUMMARY {campaignName ? <small>- [{campaignName}]</small> : ''}
            </span>
            {currentCampaignOrder && (
              <StatusTag
                startDate={currentCampaignOrder.startDate || new Date().toString()}
                endDate={currentCampaignOrder.endDate || new Date().toString()}
                statusCampaign={currentCampaignOrder.status}
                attributionWindow={currentCampaignOrder.attributionWindow || 7}
              />
            )}
            {selectedAdvertiser && (
              <span style={{
                fontSize: '14px',
                color: '#666',
                fontWeight: 'normal',
                whiteSpace: 'nowrap'
              }}>
                • {selectedAdvertiser.name}
              </span>
            )}
          </div>
        }
        style={{ marginBottom: 20 }}
        className="campaign-requester-summary"
      >

        {/* Performance Warnings */}
        {performanceWarnings.length > 0 && (
          <Alert
            message="Performance Notice"
            description={
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {performanceWarnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            }
            type="warning"
            icon={<WarningOutlined />}
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}

        {/* Campaign Statistics */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <Statistic
              title="Budget"
              value={stats.campaignOrderBudget}
              prefix="$"
              precision={0}
              titleStyle={{ fontSize: 11 }}
              valueStyle={{ fontSize: 16, color: '#1890ff' }}
            />
            <Statistic
              title="Applied Budget"
              value={stats.totalBudget}
              prefix="$"
              precision={0}
              titleStyle={{ fontSize: 11 }}
              valueStyle={{ fontSize: 16, color: stats.totalBudget > stats.campaignOrderBudget ? '#ff4d4f' : '#52c41a' }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Statistic
              title="Campaigns"
              value={stats.campaignCount}
              titleStyle={{ fontSize: 11 }}
              valueStyle={{ fontSize: 16 }}
            />
            <Statistic
              title="Avg Budget/Campaign"
              value={stats.avgBudgetPerCampaign}
              prefix="$"
              precision={0}
              titleStyle={{ fontSize: 11 }}
              valueStyle={{ fontSize: 16 }}
            />
          </div>
        </div>

        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {!currentCampaignOrder && (
            <Button
              type="default"
              block
              onClick={() => setVisibleCloneDialog(true)}
            >
              Clone
            </Button>
          )}

          <Button
            type="default"
            block
            onClick={handleSaveOptimized}
            loading={isSaving || loadingCreateCampaignOrder || loadingUpdateCampaignOrder}
            disabled={isSaving || loadingCreateCampaignOrder || loadingUpdateCampaignOrder || loadingBasicTagActivity || showActivityPopconfirm}
            icon={<SaveOutlined />}
          >
            {isSaving ? 'Saving...' : currentCampaignOrder ? 'Update' : 'Save Draft'}
          </Button>

          <Button
            type="primary"
            block
            onClick={handleNextStep}
            loading={isSaving || loadingCreateCampaignOrder || loadingUpdateCampaignOrder}
            disabled={!validationState.isComplete || isSaving || loadingBasicTagActivity || showActivityPopconfirm}
            icon={<EyeOutlined />}
          >
            Continue to Review
          </Button>

        </Space>
      </Card>

      {/* Steps Navigation - Multi-Campaign Structure */}
      <Card title="PROGRESS" style={{ marginBottom: 80 }} className="campaign-requester-summary">
        {mode === 'edit' && (
          <div style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '8px' }}>
            <Steps className="requester-summary" direction="vertical">
              {steps.map((step, index) => {
                const stepIsActive = isActive(step, index);
                return (
                  <Step
                    key={index}
                    title={
                      <a
                        href={`#${step.bookmarkId}`}
                        style={{
                          color: stepIsActive ? 'inherit' : 'rgba(0, 0, 0, 0.45)',
                          textDecoration: 'none',
                          paddingLeft: step.isNested ? '20px' : '0',
                          display: 'block',
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          const element = document.getElementById(step.bookmarkId);
                          if (element) {
                            const yOffset = -80;
                            const y =
                              element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                            window.scrollTo({ top: y, behavior: 'smooth' });
                          }
                        }}
                      >
                        {step.title}
                      </a>
                    }
                    status={stepIsActive ? 'finish' : 'wait'}
                    icon={
                      !stepIsActive ? (
                        <MinusCircleFilled style={{ fontSize: 10, color: 'rgba(0, 0, 0, 0.25)' }} />
                      ) : null
                    }
                  />
                );
              })}
            </Steps>
          </div>
        )}
        {mode === 'review' && (
          <Text>Take a moment to review the details of this request before submitting it.</Text>
        )}
      </Card>

      {/* Clone Dialog */}
      {visibleCloneDialog && (
        <CampaignRequestorCloneDialog
          visible={visibleCloneDialog}
          setVisible={setVisibleCloneDialog}
          orgId={selectedAdvertiser && selectedAdvertiser.id}
          onAddSelections={(campaignOrders) => {
            setVisibleCloneDialog(false);
            if (campaignOrders && campaignOrders.length) {
              // Use the cloning helper to bypass confirmation popups
              helpers.handleCloningOperation(() => {
                const advertiser = campaignOrders[0].advertiser;

                // Use comprehensive line item cleaning function
                const cleanedCampaignOrder = cleanLineItemsForCloning(campaignOrders[0]);

                // Remove top-level database fields
                delete cleanedCampaignOrder.id;
                delete cleanedCampaignOrder.createdAt;
                delete cleanedCampaignOrder.updatedAt;
                delete cleanedCampaignOrder.__typename;

                // Clean configuration objects
                const { transactionsConfig, signUpConfig, sessionConfig } = cleanedCampaignOrder;
                if (transactionsConfig && transactionsConfig.id) {
                  delete transactionsConfig.id;
                }
                if (signUpConfig && signUpConfig.id) {
                  delete signUpConfig.id;
                }
                if (sessionConfig && sessionConfig.id) {
                  delete sessionConfig.id;
                }

                setSelectedAdvertiser(advertiser);

                // Initialize form with cleaned data
                const clonedCampaignData = {
                  ...cleanedCampaignOrder,
                  advertiser,
                  name: `${campaignOrders[0].name} - ${new Date().getTime()}`,
                  status: 'DRAFT',
                  startDate: moment(),
                  endDate: moment().add(2, 'days'),
                  transactionsConfig,
                  signUpConfig,
                  sessionConfig,
                };

                handleInitialValues(clonedCampaignData);

                // Additional safety measure: Force UI refresh after a short delay
                setTimeout(() => {
                  const currentFormValues = helpers.form.getFieldsValue();

                  // If campaigns are still not showing, force a manual refresh
                  if (helpers.forceRefresh && (!currentFormValues.campaigns || currentFormValues.campaigns.length === 0)) {
                    helpers.forceRefresh();
                  }
                }, 300);
              });
            }
          }}
        />
      )}
    </Col>
  );
};

export default CampaignRequestorSummaryOptimized;